import { useState } from "react";
import type { FractionBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { FractionShape } from "../Visuals";
import { useCheck, CheckFooter, type CheckResult } from "../CheckShell";

interface Props {
  block: FractionBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

export function FractionBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const [shaded, setShaded] = useState<boolean[]>(() => Array(block.parts).fill(false));
  const done = check.locked;
  const count = shaded.filter(Boolean).length;
  const toggle = (i: number) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setShaded(prev => prev.map((v, j) => (j === i ? !v : v)));
  };
  const submit = () => check.submit(count === block.target);
  const shown = done && check.status === "revealed" ? Array.from({ length: block.parts }, (_, i) => i < block.target) : shaded;

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <p className="text-sm text-muted-foreground">Tap the parts to shade or unshade them.</p>
      <div className={`rounded-2xl border-2 p-4 ${done ? (check.status === "correct" ? "border-emerald-400" : "border-sky-400") : "border-border bg-card"}`}>
        <FractionShape parts={block.parts} shaded={shown} shape={block.shape} onToggle={done ? undefined : toggle} />
        <p className="text-center font-bold text-lg mt-3">
          {shown.filter(Boolean).length} of {block.parts} shaded
          <span className="text-muted-foreground font-medium"> ({shown.filter(Boolean).length}/{block.parts})</span>
        </p>
      </div>
      <CheckFooter
        check={check}
        canSubmit={true}
        onSubmit={submit}
        hint={block.hint}
        explanation={block.explanation}
        feedbackWrong={!done ? (count < block.target ? "Shade a few more parts." : "That is too many. Unshade some.") : undefined}
        revealAnswer={<p><strong>{block.target}</strong> of the {block.parts} parts should be shaded.</p>}
      />
    </div>
  );
}
